import { useEffect, useState } from "react";
import { RefreshCw, Share2 } from "lucide-react";
import { api } from "../../lib/api";
import { TILES } from "../BrandTiles";
import { BarList } from "./BarList";

const TARGETS = [
  { key: "etoro", label: "eToro" },
  { key: "results", label: "Results & testimonials" },
  { key: "telegram", label: "Telegram" },
  { key: "instagram", label: "Instagram" },
  { key: "website", label: "SYNrise website" },
  { key: "trustpilot", label: "Trustpilot" },
  { key: "share", label: "Share SYNrise" },
];
const LANGS = [
  { key: "it", label: "Italiano" },
  { key: "en", label: "English" },
  { key: "es", label: "Español" },
];
const PERIODS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 0, label: "All time" },
];

const ShareTile = () => (
  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-gold/30 bg-gold/15"><Share2 className="h-4 w-4 text-gold" /></span>
);
const iconFor = (key) => {
  const Tile = TILES[key];
  return Tile ? <Tile small /> : <ShareTile />;
};

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-[24px] border border-white/[0.07] bg-surface/70 p-5" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">{label}</p>
    <p className="mt-3 truncate font-display text-2xl font-bold text-zinc-50 sm:text-3xl">{value}</p>
  </div>
);

export const AdminDashboard = ({ onLogout }) => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (d) => {
    setLoading(true);
    api.get("/admin/stats", { params: { days: d } })
      .then((r) => setStats(r.data))
      .catch((e) => { if (e.response?.status === 401) onLogout(); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(days); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = stats?.total ?? 0;
  const byT = stats?.by_target ?? {};
  const byL = stats?.by_lang ?? {};
  const matrix = stats?.matrix ?? {};
  const top = (list, src) => { const best = [...list].sort((a, b) => (src[b.key] ?? 0) - (src[a.key] ?? 0))[0]; return src[best.key] ? best.label : "—"; };
  const targetRows = TARGETS.map((t) => ({ ...t, icon: iconFor(t.key), count: byT[t.key] ?? 0 })).sort((a, b) => b.count - a.count);
  const langRows = LANGS.map((l) => ({ ...l, icon: <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] font-mono text-xs text-zinc-200">{l.key.toUpperCase()}</span>, count: byL[l.key] ?? 0 }));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-1" data-testid="admin-period-selector">
          {PERIODS.map((p) => (
            <button key={p.days} onClick={() => setDays(p.days)} data-testid={`admin-period-${p.days}`}
              className={`h-9 rounded-full px-4 text-xs font-semibold transition-colors ${days === p.days ? "bg-gold text-ink" : "text-zinc-400 hover:text-zinc-100"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <button onClick={() => load(days)} data-testid="admin-refresh-button" aria-label="Refresh" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:text-gold">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi label="Total taps" value={total} testId="admin-kpi-total" />
        <Kpi label="Top button" value={top(TARGETS, byT)} testId="admin-kpi-top-button" />
        <Kpi label="Top language" value={top(LANGS, byL)} testId="admin-kpi-top-lang" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3"><BarList title="Taps by button" rows={targetRows} total={total} testId="admin-by-button" /></div>
        <div className="lg:col-span-2"><BarList title="Taps by language" rows={langRows} total={total} testId="admin-by-lang" /></div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-[28px] border border-white/[0.07] bg-surface/70 p-5 sm:p-6" data-testid="admin-matrix">
        <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400">Button × language</h3>
        <table className="mt-4 w-full min-w-[420px] text-sm">
          <thead>
            <tr className="text-left font-mono text-xs text-zinc-400">
              <th className="pb-3 font-normal">Button</th>
              {LANGS.map((l) => <th key={l.key} className="pb-3 text-right font-normal">{l.key.toUpperCase()}</th>)}
              <th className="pb-3 text-right font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {TARGETS.map((t) => (
              <tr key={t.key} className="border-t border-white/5" data-testid={`admin-matrix-row-${t.key}`}>
                <td className="py-3 text-zinc-200">{t.label}</td>
                {LANGS.map((l) => <td key={l.key} className="py-3 text-right font-mono text-zinc-300">{matrix[t.key]?.[l.key] ?? 0}</td>)}
                <td className="py-3 text-right font-mono font-semibold text-gold">{byT[t.key] ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
